import { GridColDef } from '@mui/x-data-grid-premium'
import type { LostAnchorDataDTO, LostShearAnchorSelectionDTO } from '@api/fastAPI.schemas'

export interface LostAnchorDataRow {
  id: string
  kind: 'Zuganker' | 'Schubanker'
  wall: string
  anchor: string
  side: string
  comment: string
  /** The connector set by hand on this anchor, '' when the prioritization chooses. */
  userDefinedConnector: string
  loadSum: number | null
  [loadField: `load_${number}` | `formula_${number}` | `source_${number}`]:
    | string
    | number
    | undefined
}

const SIDE_LABEL: Record<LostAnchorDataDTO['side'], string> = {
  links: 'links',
  rechts: 'rechts',
  unten: 'unten',
}

const POSITION_LABEL: Record<LostShearAnchorSelectionDTO['position'], string> = {
  bottom: 'Wandfuß',
  top: 'Wandkopf',
}

// Manual loads are stored in Newtons, the UI shows kN
const NEWTON_PER_KILONEWTON = 1000

const loadFormatter = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 })

const formatLoad = (value: number | null | undefined) =>
  value === null || value === undefined ? '' : loadFormatter.format(value)

export const buildLostAnchorDataRows = (
  lostAnchorData: LostAnchorDataDTO[],
  elementLabel: (guid: string) => string,
  connectorLabel: (guid: string) => string,
): LostAnchorDataRow[] =>
  lostAnchorData.map(item => {
    const row: LostAnchorDataRow = {
      id: item.support_guid,
      kind: 'Zuganker',
      wall: elementLabel(item.element_guid),
      anchor: item.segment_guid ? elementLabel(item.segment_guid) : elementLabel(item.element_guid),
      side: SIDE_LABEL[item.side],
      comment: item.comment,
      userDefinedConnector: item.userDefinedConnectorId
        ? connectorLabel(item.userDefinedConnectorId)
        : '',
      loadSum: item.manual_loads.length
        ? item.manual_loads.reduce((sum, load) => sum + load.load / NEWTON_PER_KILONEWTON, 0)
        : null,
    }
    item.manual_loads.forEach((load, index) => {
      row[`load_${index + 1}`] = load.load / NEWTON_PER_KILONEWTON
      row[`formula_${index + 1}`] = load.load_formula
      row[`source_${index + 1}`] = load.source
    })
    return row
  })

export const buildLostShearAnchorRows = (
  lostShearAnchors: LostShearAnchorSelectionDTO[],
  elementLabel: (guid: string) => string,
  shearConnectorLabel: (guid: string) => string,
): LostAnchorDataRow[] =>
  lostShearAnchors.map(item => ({
    id: `${item.segment_guid}-${item.position}`,
    kind: 'Schubanker',
    wall: elementLabel(item.element_guid),
    anchor: elementLabel(item.segment_guid),
    side: POSITION_LABEL[item.position],
    comment: '',
    userDefinedConnector: shearConnectorLabel(item.userDefinedConnectorId),
    loadSum: null,
  }))

export const buildLostAnchorDataColumns = (
  lostAnchorData: LostAnchorDataDTO[],
): GridColDef<LostAnchorDataRow>[] => {
  const maxLoadCount = Math.max(0, ...lostAnchorData.map(item => item.manual_loads.length))
  const loadColumns = Array.from({ length: maxLoadCount }, (_, index) => {
    const number = index + 1
    const columns: GridColDef<LostAnchorDataRow>[] = [
      {
        field: `load_${number}`,
        headerName: `Last ${number} [kN]`,
        type: 'number',
        width: 110,
        valueFormatter: (value: number | undefined) => formatLoad(value),
      },
      { field: `formula_${number}`, headerName: `Formel ${number}`, width: 120 },
      { field: `source_${number}`, headerName: `Quelle ${number}`, width: 140 },
    ]
    return columns
  }).flat()

  return [
    { field: 'kind', headerName: 'Art', width: 100 },
    { field: 'wall', headerName: 'Wand', width: 110 },
    { field: 'anchor', headerName: 'Anker', width: 130 },
    { field: 'side', headerName: 'Lage', width: 90 },
    {
      field: 'loadSum',
      headerName: 'Summe manuelle Lasten [kN]',
      type: 'number',
      width: 200,
      valueFormatter: (value: number | null) => formatLoad(value),
    },
    { field: 'userDefinedConnector', headerName: 'Festgelegter Verbinder', width: 190 },
    { field: 'comment', headerName: 'Kommentar', flex: 1, minWidth: 200 },
    ...loadColumns,
  ]
}
